import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { MainContainer, MainHeaderContainer } from 'layouts/components';
import Utils from 'utilities/helper.util';
import { EnquiryInformation } from 'components';
import { selectNewEnquiryDetails } from 'redux/lodgeEnquiry/selectors';
import {
  selectedEnquiry,
  selectEnquiryLoader
} from 'redux/enquiries/selectors';
import {
  selectScreenSize,
  selectIsFromSwxLocate
} from 'redux/session/selectors';
import {
  ReferralsList,
  AboutAndADS,
  GetPlans,
  MobileJobDetails
} from './components';
import {
  connectWebsocketForEnquiry,
  selectLodgeEnquiry
} from 'redux/enquiries';
import CLOModal from './components/AboutAndADS/CLOModal';
import { submitCollect } from 'redux/track/actions';
import { EntityType } from 'redux/track/types';
import {
  getLocationTypesCodeMsg,
  getActivityTypesCodeMsg
} from 'appConstants/enquiry';
import { getISPLink } from 'appConstants/ads';
import { useDeviceType } from 'customHooks/useDeviceType';
import { useLualExtent } from 'components/EnquiryInformation/useLualExtent';

type Page =
  | 'overview'
  | 'getPlans'
  // Mobile only - show enquiry details in a proper page instead of extending it from the header
  | 'mobile-job-details'
  // Mobile only - show a mobile-optimized version of the getPlans page
  | 'mobile-getPlans'
  // Mobile only - show download and tools separately from the rest of the getPlans
  | 'mobile-download-and-tools';

const getInitialPage = (
  isModal: boolean,
  isDisplay: boolean,
  isMobileOrTablet: boolean
) => {
  if ((isModal || isDisplay) && !isMobileOrTablet) {
    return 'getPlans';
  }

  if ((isModal || isDisplay) && isMobileOrTablet) {
    return 'mobile-getPlans';
  }

  return 'overview';
};

export const EnquiryDetailsNew: React.FC<Props | any> = (props: Props) => {
  const {
    isModal = false,
    isDisplay = false,
    onClickClose,
    setIsDownloadPdf,
    setIsDownloadZip,
    shareId = ''
  } = props;

  const dispatch = useDispatch();
  const { isMobileOrTablet } = useDeviceType();

  const bodyDivRef = useRef<HTMLDivElement>(null);
  const enquiry: any = useSelector(selectedEnquiry);
  const isLoading: any = useSelector(selectEnquiryLoader);
  const newEnquiryDetails: any = useSelector(selectNewEnquiryDetails);
  const screenSize: any = useSelector(selectScreenSize);
  const lodgeEnquiry: any = useSelector(selectLodgeEnquiry);
  const isFromSwxLocate = useSelector(selectIsFromSwxLocate);
  const [enquiryDetail, setEnquiryDetail] = useState<any>();
  const [referralsObj, setReferralsObj] = useState<any>();

  const initialPage = getInitialPage(isModal, isDisplay, isMobileOrTablet);
  const [page, setPage] = useState<Page>(initialPage);
  // In mobile view, only "overview" and "mobile-getPlans" are considered parent pages.
  // While "mobile-job-details" and "mobile-download-and-tools" are technically separated pages,
  // visually, they appeared as expanded panel from the header (EnquiryInformation) component.
  // So, we need to track the parent page to correctly navigate back to the correct page.
  const [parentPage, setParentPage] = useState<Page>(initialPage);

  // Page mapping when device type changes (e.g., orientation change)
  const getCorrespondingPage = (
    currentPage: Page,
    isNowMobileOrTablet: boolean
  ): Page => {
    if (isNowMobileOrTablet) {
      // Desktop -> Mobile/Tablet transitions
      switch (currentPage) {
        case 'getPlans':
          return 'mobile-getPlans';
        case 'overview':
          return 'overview'; // Same for both
        default:
          return currentPage; // Keep mobile pages as is
      }
    } else {
      // Mobile/Tablet -> Desktop transitions
      switch (currentPage) {
        case 'mobile-getPlans':
          return 'getPlans';
        case 'mobile-job-details':
          return 'overview'; // Job details are in header/collapsible on desktop
        case 'mobile-download-and-tools':
          return 'getPlans'; // Download tools are in sidebar on desktop
        case 'overview':
          return 'overview'; // Same for both
        default:
          return currentPage; // Keep desktop pages as is
      }
    }
  };

  // Handle orientation changes - sync page state with device type changes
  // This ensures the correct view is shown when screen size changes (e.g., iPad rotation)
  useEffect(() => {
    const correspondingPage = getCorrespondingPage(page, isMobileOrTablet);
    if (correspondingPage !== page) {
      setPage(correspondingPage);

      // Update parent page for mobile navigation logic
      if (isMobileOrTablet) {
        // Set parent page for mobile sub-pages
        if (correspondingPage === 'mobile-getPlans') {
          setParentPage('mobile-getPlans');
        } else if (correspondingPage === 'overview') {
          setParentPage('overview');
        }
      } else {
        // For desktop, parent page concept doesn't apply
        setParentPage(correspondingPage);
      }

      // Update header title to match new page
      if (
        correspondingPage === 'getPlans' ||
        correspondingPage === 'mobile-getPlans'
      ) {
        setHeaderTitle('Plans');
      } else if (correspondingPage === 'overview') {
        setHeaderTitle(isModal || isDisplay ? 'Enquiry' : 'Enquiry sent');
      }
    }
  }, [isMobileOrTablet, page, isModal, isDisplay]);

  const [headerTitle, setHeaderTitle] = useState<string>(
    isModal || isDisplay ? 'Plans' : 'Enquiry sent'
  );
  const [connectedWebSocket, setConnectedWebSocket] = useState<boolean>(false);
  const [responsesExpired, setResponsesExpired] = useState<boolean>(false);
  const [enquiryValidityDate, setEnquiryValidityDate] = useState<string>('');
  const [timestamp, setTimestamp] = useState<Date>();
  const [isCLOModalOpen, setIsCLOModalOpen] = useState<boolean>(false);
  const [isEnquiryLoading, setIsEnquiryLoading] = useState<boolean>(true);
  const [
    selectedReferralExternalId,
    setSelectedReferralExternalId
  ] = useState<number>(0);
  const [
    showResponsesExpiredModal,
    setShowResponsesExpiredModal
  ] = useState<boolean>(false);
  const [
    selectedReferralOverview,
    setSelectedReferralOverview
  ] = useState<number>(-1);
  const [lualExtent, setLualExtent] = useState<any>(null);

  const [lualZoomLevel, setLualZoomLevel] = useState<number | null>(null);

  // Use the enhanced useLualExtent hook
  const { lualZoomLevel: hookLualZoomLevel } = useLualExtent(
    enquiryDetail?.shape,
    setLualExtent
  );

  const handleMiniMapLoaded = (zoomLevel: number) => {
    setLualZoomLevel(zoomLevel);
  };

  // Use the hook's zoom level directly - no need for manual state
  const finalLualMapZoomLevel = hookLualZoomLevel;

  const onClickCloseResponseExpiredModal = () => {
    setShowResponsesExpiredModal(false);
  };

  const handleCLOOpenModal = () => {
    setIsCLOModalOpen(true);
  };

  const handleCLOCloseModal = () => {
    setIsCLOModalOpen(false);
  };

  const enquiryInformationRef = useRef<HTMLDivElement>(null);

  const goToTop = () => {
    if (bodyDivRef?.current) {
      bodyDivRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGetPlansClick = useCallback(() => {
    goToTop();
    setTimeout(() => {
      dispatch(
        submitCollect({
          payload: { enquiryId: enquiryDetail.id },
          type: EntityType.VIEW_RESPONSE
        })
      );
      const getPlansPage = isMobileOrTablet ? 'mobile-getPlans' : 'getPlans';
      setParentPage(getPlansPage);
      setPage(getPlansPage);
      setHeaderTitle('Plans');
    }, 300);
    setTimestamp(new Date());
  }, [dispatch, enquiryDetail, isMobileOrTablet]);

  const handleBackButtonClick = () => {
    goToTop();
    setSelectedReferralExternalId(0);
    setSelectedReferralOverview(-1);
    setTimeout(() => {
      setPage('overview');
      setParentPage('overview');
      setHeaderTitle(isModal || isDisplay ? 'Enquiry' : 'Enquiry sent');
    }, 300);
  };

  const handleMobileJobDetailsClick = () => {
    goToTop();
    setPage('mobile-job-details');
    setHeaderTitle('Job details');
  };

  const handleMobileDownloadAndToolsClick = () => {
    goToTop();
    setPage('mobile-download-and-tools');
    setHeaderTitle('Download & tools');
  };

  const duplicateReferral = (referrals: any) => {
    const duplicateItems = referrals
      .map((e: any) => e.organisationId)
      .map((e: any, i: any, final: any) => final.indexOf(e) !== i && i)
      .filter((obj: any) => referrals[obj])
      .map((e: any) => referrals[e]);

    updateAttribute(referrals, duplicateItems);
  };

  const updateAttribute = (referrals: any, duplicateReferrals: any) => {
    const nameCount: { [key: string]: number } = {};

    referrals
      .sort((a: any, b: any) => a.externalId - b.externalId)
      .forEach((referral: any) => {
        const foundItem = duplicateReferrals.find(
          (duplicateReferral: any) =>
            referral.organisationId === duplicateReferral.organisationId
        );
        if (foundItem) {
          if (isNaN(nameCount[referral.organisationId])) {
            nameCount[referral.organisationId] = 1;
          } else {
            nameCount[referral.organisationId]++;
          }

          if (
            !referral.name.includes(`(${nameCount[referral.organisationId]})`)
          ) {
            referral.name = `${referral.name} (${
              nameCount[referral.organisationId]
            })`;
          }
        }
      });
  };

  const handleContactsSelection = (selected: string) => {
    const [type, value, referralExternalId] = selected.split('|');
    if (value !== '-') {
      const anchor = document.createElement('a');
      switch (type) {
        case 'phone':
        case 'emergency':
          anchor.href = `tel:${value}`;
          anchor.target = '_parent';
          anchor.click();
          break;
        case 'web':
          anchor.href = value;
          anchor.target = '_blank';
          anchor.click();
          break;
        case 'email': {
          const jobNo = enquiryDetail?.externalId
            ? `BYDA Job ID: ${enquiryDetail?.externalId} `
            : '';
          const referralId = `Referral ID: ${
            selectedReferralExternalId !== 0
              ? selectedReferralExternalId
              : referralExternalId
          }`;
          anchor.href = `mailTo:${encodeURIComponent(
            value
          )}?subject=${jobNo}${referralId}`;
          anchor.target = '_parent';
          anchor.click();
          break;
        }
      }
    }
  };

  const handleClickCloseModal = () => {
    setResponsesExpired(false);
    setShowResponsesExpiredModal(false);
    onClickClose();
  };

  const handleOnClickISPLink = () => {
    const linkURL = getISPLink('plan-enquiry-overview');
    window.open(linkURL, '_blank');
  };

  const combineNCleanWorkplaceLocationArray = (
    array1: string[],
    array2: string[]
  ) => {
    const combinedArray = array1.concat(array2);
    const filteredArray = combinedArray.filter(
      (item) => item !== 'Road reserve'
    );
    return filteredArray;
  };

  useEffect(() => {
    if (selectedReferralOverview >= 0) {
      handleGetPlansClick();
    }
  }, [selectedReferralOverview, handleGetPlansClick]);

  useEffect(() => {
    let newReferralsObj: any = [];
    if (!Utils.isObjectEmpty(newEnquiryDetails) || enquiry) {
      if (newEnquiryDetails?.Referrals) {
        const { Referrals: referralsArr, isSandboxTest } = newEnquiryDetails;
        newReferralsObj = referralsArr.map((item: any) => ({
          id: 'item.id',
          isNewResponses: item?.isNewResponses,
          status: item.status,
          externalId: item.externalId,
          contactPhoneNumber: item.Organisation.contactPhoneNumber,
          contactEmergencyPhone: item.Organisation.contactEmergencyPhone,
          contactEmail: item.Organisation?.contactEmail,
          name: item.Organisation?.name,
          waittime: item?.Organisation?.TypicalWaitTime?.details?.P90 || null,
          organisationType: item.Organisation?.organisationType,
          isSandboxTest: isSandboxTest,
          notices: item.Organisation?.Notices,
          organisationId: item.Organisation?.id,
          contactUrl: item.Organisation?.contactUrl,
          safetyMessage: item.Organisation?.safetyMessage,
          logo: item.Organisation?.LogoImageFile?.id,
          updatedAt: item.updatedAt,
          responses: item.Responses?.map((res: any) => {
            const {
              id,
              InboundEmail,
              inboundEmailId,
              OutboundEmails,
              createdAt,
              created_at,
              custom_properties
            } = res;
            const outboundEmailId = OutboundEmails.map((email: any) => {
              const { id } = email;
              return id;
            });

            const inboundEmailIdFromObject = InboundEmail?.id || null;

            return {
              responseId: id,
              inboundEmailId: inboundEmailId || inboundEmailIdFromObject,
              outboundEmailId: outboundEmailId[0],
              createdAt: createdAt || created_at,
              custom_properties,
              primaryContent: item.ReferralConfiguration?.primaryContent
            };
          })
        }));
      } else {
        newReferralsObj = enquiry?.referrals || [];
      }
      if (!Utils.isObjectEmpty(newReferralsObj)) {
        duplicateReferral(newReferralsObj);
      }
      // check for response expiry
      const enquiryCreatedAt = !Utils.isObjectEmpty(newEnquiryDetails)
        ? newEnquiryDetails.createdAt
        : enquiry.createdAt;
      const responseExpiredDate = dayjs(enquiryCreatedAt).add(90, 'day');
      const dateToday = dayjs().endOf('day');
      if (dateToday > responseExpiredDate) {
        setResponsesExpired(true);
        setShowResponsesExpiredModal(true);
      } else {
        setResponsesExpired(false);
        setShowResponsesExpiredModal(false);
      }
      const enquiryValidUntil = dayjs(enquiryCreatedAt).add(28, 'day');
      setEnquiryValidityDate(enquiryValidUntil.format('DD MMM YYYY'));
    }

    const enquiryData = !Utils.isObjectEmpty(newEnquiryDetails)
      ? newEnquiryDetails
      : enquiry;
    let enquiryDataUpdated = enquiryData;

    if (enquiryData && !Utils.isObjectEmpty(enquiryData)) {
      const defaultDesignAct: any = [
        { code: 'CONVEYANCING', label: 'Conveyancing' },
        { code: 'PLANNING_DESIGN', label: 'Planning & Design' },
        { code: 'SUBDIVISION', label: 'Subdivision' },
        { code: 'TENDERING', label: 'Tendering' }
      ];

      const defaultExcavationAct: any = [
        { code: 'BLASTING', label: 'Blasting' },
        { code: 'HORIZ_BORING', label: 'Horizontal Boring' },
        { code: 'MANUAL_EXCAVATION', label: 'Manual Excavation' },
        { code: 'MECH_EXCAVATION', label: 'Mechanical Excavation' },
        { code: 'NON_DESTRUCTIVE_DIGGING', label: 'Non-Destructive Digging' },
        { code: 'SUBDIVISION', label: 'Subdivision' },
        { code: 'VERTICAL_BORING', label: 'Vertical Boring' }
      ];

      const {
        designAct = defaultDesignAct,
        excavationAct = defaultExcavationAct
      } = lodgeEnquiry || {};
      const {
        locationTypes,
        locationsInRoad,
        activityTypes,
        isPlanningJob
      } = enquiryData;
      const newLocationTypes: string[] = (
        locationTypes ?? []
      ).map((code: string) => getLocationTypesCodeMsg(code));
      let newActivityTypes: string[] = [];
      (activityTypes ?? []).forEach((activity: string) => {
        if (activity.search(',') !== -1) {
          const activityArr = activity
            .split(',')
            .map((item: string) =>
              getActivityTypesCodeMsg(
                item.toUpperCase(),
                designAct,
                excavationAct,
                isPlanningJob
              )
            );
          newActivityTypes = [...newActivityTypes, ...activityArr];
        } else {
          newActivityTypes = [
            ...newActivityTypes,
            getActivityTypesCodeMsg(
              activity.toUpperCase(),
              designAct,
              excavationAct,
              isPlanningJob
            )
          ];
        }
      });

      const locationsInRoadCapilizeFirstLetter = locationsInRoad?.map(
        (word: any) => {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
      );

      const workplaceLocationCombine = combineNCleanWorkplaceLocationArray(
        newLocationTypes,
        locationsInRoadCapilizeFirstLetter
      );

      enquiryDataUpdated = {
        ...enquiryData,
        locationTypes: newLocationTypes.join(', '),
        locationsInRoad: Array.isArray(locationsInRoadCapilizeFirstLetter)
          ? locationsInRoadCapilizeFirstLetter.join(', ')
          : '',
        workplaceLocation: workplaceLocationCombine.join(', '),
        activityTypes: newActivityTypes
      };
    }

    setEnquiryDetail(enquiryDataUpdated);
    if (newReferralsObj?.length > 0) {
      newReferralsObj.sort((a: any, b: any) => {
        if (a.organisationId !== b.organisationId) {
          return a.name.localeCompare(b.name);
        } else {
          return a.externalId - b.externalId;
        }
      });
      setReferralsObj(newReferralsObj);
    }
    // eslint-disable-next-line
  }, [enquiry, newEnquiryDetails]);

  useEffect(() => {
    if (!connectedWebSocket && enquiryDetail?.id) {
      dispatch(connectWebsocketForEnquiry(enquiryDetail.id));
      setConnectedWebSocket(true);
    }
  }, [enquiryDetail, connectedWebSocket, dispatch]);

  useEffect(() => {
    if (!isLoading && enquiryDetail?.createdAt) {
      setIsEnquiryLoading(false);
    } else {
      setIsEnquiryLoading(true);
    }
  }, [isLoading, enquiryDetail]);

  return (
    <div
      ref={bodyDivRef}
      id="enquiryDetailsNew"
      data-testid="enquiryDetailsNew"
      className={`enquiryDetailsNew--wrapper container--wrapper ${
        isModal ? 'enquiryDetailsNew-modal-style' : ''
      }
        ${isFromSwxLocate ? 'locate-wrapper' : ''} ${
        isMobileOrTablet ? 'mobile-view-enquiryDetailsNew--wrapper' : ''
      }`}
    >
      {!isModal && <MainHeaderContainer enableHeaderGlobalBar={true} />}
      <MainContainer>
        <div
          className={`main-wrapper ${
            isMobileOrTablet ? 'mobile-view-main-wrapper' : ''
          }`}
        >
          <div
            ref={enquiryInformationRef}
            className={`${
              isModal
                ? 'enquiry-information-wrapper-modal'
                : 'enquiry-information-wrapper'
            }${isMobileOrTablet ? ' is-mobile-or-tablet' : ''}`}
          >
            <EnquiryInformation
              page={page}
              parentPage={parentPage}
              enquiryDetail={enquiryDetail}
              headerTitle={headerTitle}
              showBackNavigation={page === 'getPlans'}
              screenSize={screenSize}
              showGetPlansButton={page === 'overview' && referralsObj}
              handleGetPlansClick={handleGetPlansClick}
              handleBackButtonClick={handleBackButtonClick}
              handleMobileJobDetailsClick={handleMobileJobDetailsClick}
              handleMobileDownloadAndToolsClick={
                handleMobileDownloadAndToolsClick
              }
              isLoading={isEnquiryLoading}
              isModal={isModal}
              onClickClose={handleClickCloseModal}
              responsesExpired={responsesExpired && page === 'getPlans'}
              showResponsesExpiredModal={
                showResponsesExpiredModal && page === 'getPlans'
              }
              shareId={shareId}
              bodyDivRef={bodyDivRef}
              setLualExtent={setLualExtent}
              onMiniMapLoaded={handleMiniMapLoaded}
            />
          </div>
          {page === 'overview' && (
            <>
              <div
                className={`referrals-list-wrapper ${
                  isMobileOrTablet ? 'is-mobile-or-tablet' : ''
                }`}
              >
                <ReferralsList
                  referrals={referralsObj}
                  screenSize={screenSize}
                  isLoading={isEnquiryLoading}
                  handleContactsSelection={handleContactsSelection}
                  enquiryValidityDate={enquiryValidityDate}
                  setSelectedReferralOverview={setSelectedReferralOverview}
                />
              </div>
              <AboutAndADS
                goToTop={goToTop}
                screenSize={screenSize}
                enquiryDetail={enquiryDetail}
                handleGetPlansClick={handleGetPlansClick}
                referrals={referralsObj}
                handleOnClickISPLink={handleOnClickISPLink}
                handleCLOOpenModal={handleCLOOpenModal}
              />
            </>
          )}
          {[
            'getPlans',
            'mobile-getPlans',
            'mobile-download-and-tools'
          ].includes(page) && (
            <div
              className={`get-plans-wrapper ${
                isMobileOrTablet ? 'mobile-view-get-plans-wrapper' : ''
              }`}
            >
              <GetPlans
                page={page}
                enquiryInformationRef={enquiryInformationRef}
                creatorUserId={enquiryDetail?.userId}
                referrals={referralsObj}
                enquiryCreatedAt={enquiryDetail?.createdAt}
                screenSize={screenSize}
                handleBackButtonClick={handleBackButtonClick}
                goToTop={goToTop}
                enquiryDetail={enquiryDetail}
                enquiryId={enquiryDetail?.id}
                responsesExpired={responsesExpired}
                showResponsesExpiredModal={showResponsesExpiredModal}
                isModal={isModal}
                handleCLOOpenModal={handleCLOOpenModal}
                isLoading={isEnquiryLoading}
                timestamp={timestamp}
                setSelectedReferralExternalId={setSelectedReferralExternalId}
                handleContactsSelection={handleContactsSelection}
                onClickClose={handleClickCloseModal}
                shareId={shareId}
                onClickCloseResponseExpiredModal={
                  onClickCloseResponseExpiredModal
                }
                setIsDownloadPdf={setIsDownloadPdf}
                setIsDownloadZip={setIsDownloadZip}
                selectedReferralOverview={selectedReferralOverview}
                setSelectedReferralOverview={setSelectedReferralOverview}
                enquiryValidityDate={enquiryValidityDate}
                lualExtent={lualExtent}
                onMiniMapLoaded={handleMiniMapLoaded}
                lualZoomLevel={lualZoomLevel}
                lualMapZoomLevel={finalLualMapZoomLevel}
              />
            </div>
          )}
          {page === 'mobile-job-details' && (
            <MobileJobDetails
              enquiryDetail={enquiryDetail}
              setLualExtent={setLualExtent}
              enquiryValidityDate={enquiryValidityDate}
              isLoading={isLoading}
            />
          )}
        </div>
      </MainContainer>
      <CLOModal
        isOpen={isCLOModalOpen}
        onClose={handleCLOCloseModal}
        enquiryDetail={enquiryDetail}
        screenSize={screenSize}
      />
    </div>
  );
};

type ownProps = {
  isModal: boolean;
  isDisplay: boolean;
  onClickClose?: any;
  setIsDownloadPdf?: any;
  setIsDownloadZip?: any;
  shareId?: string;
};

type Props = ownProps;

EnquiryDetailsNew.displayName = 'EnquiryDetailsNew';

export default React.memo(EnquiryDetailsNew);
